import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  KeyRound,
  Loader2,
  Mail,
  RefreshCw,
  Send,
  ShieldCheck,
  UserPlus,
  UserRound,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const formatDateTime = (value) => {
  if (!value) return 'Never';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Never';
    return date.toLocaleString();
  } catch {
    return 'Never';
  }
};

const statusBadge = (owner) => {
  if (!owner) return null;
  if (owner.is_active) {
    return (
      <Badge className="bg-emerald-100 text-emerald-700" data-testid="owner-status-badge">
        Active
      </Badge>
    );
  }
  return (
    <Badge className="bg-slate-100 text-slate-600" data-testid="owner-status-badge">
      {owner.status || 'Inactive'}
    </Badge>
  );
};

/**
 * Agency Owner Login Section
 *
 * Shows the agency owner's authentication account (users collection) —
 * deliberately separate from the agency contact email, which is
 * communications metadata only.
 */
const AgencyOwnerLoginSection = ({ agencyId, token, isReadOnly }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [data, setData] = useState(null);

  const [sendingReset, setSendingReset] = useState(false);

  const [changeOpen, setChangeOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [changeStep, setChangeStep] = useState('form'); // form | confirm
  const [submittingChange, setSubmittingChange] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [submittingInvite, setSubmittingInvite] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadOwnerLogin = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await axios.get(`${API}/admin/agencies/${agencyId}/owner-login`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error('Failed to load owner login info:', err);
      setLoadError(err.response?.data?.detail || 'Failed to load owner login information');
    } finally {
      setLoading(false);
    }
  }, [agencyId, token]);

  useEffect(() => {
    loadOwnerLogin();
  }, [loadOwnerLogin]);

  const owner = data?.owner;
  const pendingChange = owner?.pending_email_change;

  const handleSendReset = async () => {
    setSendingReset(true);
    try {
      await axios.post(
        `${API}/admin/agencies/${agencyId}/owner-login/send-password-reset`,
        {},
        { headers },
      );
      toast.success(`Password reset sent to ${owner?.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send password reset');
    } finally {
      setSendingReset(false);
    }
  };

  const openChangeDialog = () => {
    setNewEmail('');
    setChangeStep('form');
    setChangeOpen(true);
  };

  const handleChangeSubmit = async () => {
    setSubmittingChange(true);
    try {
      const res = await axios.post(
        `${API}/admin/agencies/${agencyId}/owner-login/request-email-change`,
        { new_email: newEmail.trim(), confirm: true },
        { headers },
      );
      toast.success(res.data?.message || 'Verification sent to the new email.');
      setChangeOpen(false);
      loadOwnerLogin();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to request sign-in email change');
    } finally {
      setSubmittingChange(false);
    }
  };

  const handleCancelPending = async () => {
    try {
      await axios.post(
        `${API}/admin/agencies/${agencyId}/owner-login/cancel-email-change`,
        {},
        { headers },
      );
      toast.success('Pending sign-in email change cancelled');
      loadOwnerLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel pending change');
    }
  };

  const handleInvite = async () => {
    setSubmittingInvite(true);
    try {
      const res = await axios.post(
        `${API}/admin/agencies/${agencyId}/owner-login/invite`,
        { email: inviteEmail.trim(), name: inviteName.trim() || undefined },
        { headers },
      );
      toast.success(res.data?.message || 'Owner invited');
      setInviteOpen(false);
      loadOwnerLogin();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to invite owner');
    } finally {
      setSubmittingInvite(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="agency-owner-login-section">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          <ShieldCheck size={14} className="inline mr-1" />
          Agency Owner Login
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={loadOwnerLogin}
          disabled={loading}
          data-testid="owner-login-refresh"
        >
          <RefreshCw size={14} />
        </Button>
      </div>
      <p className="text-xs text-slate-500 -mt-2">
        The sign-in email below belongs to the owner&apos;s user account. It is managed
        separately from the agency contact email above.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-4" data-testid="owner-login-loading">
          <Loader2 size={16} className="animate-spin" />
          Loading owner account…
        </div>
      ) : loadError ? (
        <div className="text-sm text-red-600 py-2" data-testid="owner-login-error">
          {String(loadError)}
        </div>
      ) : owner ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <div className="text-xs text-slate-500 mb-0.5">
                <UserRound size={12} className="inline mr-1" />
                Owner
              </div>
              <div className="font-medium text-slate-900" data-testid="owner-name">
                {owner.name || '—'}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-0.5">
                <Mail size={12} className="inline mr-1" />
                Login Email
              </div>
              <div className="font-medium text-slate-900" data-testid="owner-login-email">
                {owner.email}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-0.5">Account Status</div>
              {statusBadge(owner)}
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-0.5">Last Login</div>
              <div className="text-slate-700" data-testid="owner-last-login">
                {formatDateTime(owner.last_login)}
              </div>
            </div>
          </div>

          {pendingChange && !pendingChange.expired && (
            <div
              className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center justify-between gap-2"
              data-testid="owner-pending-email-change"
            >
              <span>
                Pending sign-in email change to <strong>{pendingChange.pending_email}</strong> —
                awaiting verification. The current login stays active until confirmed.
              </span>
              {!isReadOnly && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-amber-800 shrink-0"
                  onClick={handleCancelPending}
                  data-testid="owner-cancel-pending-change"
                >
                  Cancel
                </Button>
              )}
            </div>
          )}

          {!isReadOnly && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendReset}
                disabled={sendingReset}
                data-testid="owner-send-password-reset"
              >
                {sendingReset ? (
                  <Loader2 size={14} className="mr-2 animate-spin" />
                ) : (
                  <Send size={14} className="mr-2" />
                )}
                Send password reset
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={openChangeDialog}
                data-testid="owner-change-login-email"
              >
                <KeyRound size={14} className="mr-2" />
                Change login email
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-4 text-sm text-slate-600 space-y-3">
          <p data-testid="owner-login-none">
            No owner login account exists for this agency yet. The contact email above is
            not a sign-in account.
          </p>
          {!isReadOnly && (
            <Button size="sm" onClick={() => setInviteOpen(true)} data-testid="owner-invite-button">
              <UserPlus size={14} className="mr-2" />
              Invite owner
            </Button>
          )}
        </div>
      )}

      {/* Change login email dialog — explicit two-step confirmation */}
      <Dialog open={changeOpen} onOpenChange={setChangeOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          {changeStep === 'form' ? (
            <>
              <DialogHeader>
                <DialogTitle>Change login email</DialogTitle>
                <DialogDescription>
                  This changes how the agency owner signs in. It does not change the agency
                  contact email.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-2">
                <Label htmlFor="owner-new-login-email">New login email</Label>
                <Input
                  id="owner-new-login-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="owner@newdomain.com"
                  className="bg-white"
                  data-testid="owner-new-login-email-input"
                />
                <p className="text-xs text-slate-500">
                  A verification link will be sent to the new address. The current sign-in
                  email keeps working until the new one is verified.
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setChangeOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => setChangeStep('confirm')}
                  disabled={!newEmail.trim() || !newEmail.includes('@')}
                  data-testid="owner-change-email-continue"
                >
                  Continue
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Update the agency owner&apos;s sign-in email too?</DialogTitle>
                <DialogDescription data-testid="owner-change-email-confirm-text">
                  The owner&apos;s sign-in email will change to{' '}
                  <strong>{newEmail.trim().toLowerCase()}</strong> after the new address is
                  verified. Their password, role, and agency membership are unchanged.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setChangeStep('form')}>
                  Back
                </Button>
                <Button
                  onClick={handleChangeSubmit}
                  disabled={submittingChange}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="owner-change-email-confirm"
                >
                  {submittingChange && <Loader2 size={14} className="mr-2 animate-spin" />}
                  Yes, send verification
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Invite owner dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite agency owner</DialogTitle>
            <DialogDescription>
              Creates a login account for this agency&apos;s owner and emails them an
              invitation to set a password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label htmlFor="owner-invite-name">Owner name</Label>
              <Input
                id="owner-invite-name"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Jane Doe"
                className="bg-white"
                data-testid="owner-invite-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="owner-invite-email">Login email</Label>
              <Input
                id="owner-invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="owner@agency.com"
                className="bg-white"
                data-testid="owner-invite-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              disabled={submittingInvite || !inviteEmail.trim() || !inviteEmail.includes('@')}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="owner-invite-submit"
            >
              {submittingInvite && <Loader2 size={14} className="mr-2 animate-spin" />}
              Send invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgencyOwnerLoginSection;
